import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { components, InputProps, MultiValueGenericProps } from 'react-select';
import { BUTTON_VARIANTS } from 'ui/button';
import { BaseOption } from 'ui/selectv2/types';
import noop from 'utils/noop';

import { TAG_SIZES, TagSize } from '../constants';
import { Tag } from '../Tag/Tag';
import {
  StyledActionButton,
  StyledMultiSelect,
  TagGroupActionButtons,
  TagGroupWrapper,
  TagGroupEditWrapper,
} from '../Tags.styles';
import { TagList } from './TagList';
import {
  generateTagOptions,
  getFilteredTags,
  getTagText,
  tagToVisibleTagOption,
  tagGroupSelectCustomTheme,
} from './utils';
import { TagGroupValue, TagOption } from '../types';

/**
 * Custom input component for the TagGroup's multi-select dropdown.
 */
const TagGroupInput = (props: InputProps<BaseOption>) => {
  return (
    <components.Input
      {...props}
      placeholder={props?.hasValue ? (props?.selectProps?.placeholder as any) : ''}
      data-test="add-tags-dropdown-trigger"
    />
  );
};

export type TagGroupProps = {
  /** Class name for the tag group container */
  className?: string;
  /** Should this automatically save the changes on blur? */
  isAutoSaveEnabled?: boolean;
  /** True if the tag group is editable, only for standalone pages */
  isEditable?: boolean;
  /** List of selected tags */
  value?: TagGroupValue[];
  /** Size of the tags in the tag group */
  tagSize?: TagSize;
  /** Maximum number of tags to display */
  maxTags?: number;
  /**
   * Array of available tag strings that users can select from in edit mode.
   * Should NOT include tags that are already in the `value` array.
   */
  tags?: TagOption[];
  /**
   * Placeholder text shown in the dropdown input.
   */
  placeholder?: string;
  /**
   * Text displayed when there are no available options.
   */
  emptyOptionsPlaceholder?: string;
  /**
   * Callback invoked when tags are modified.
   */
  onUpdate?: (tags: TagOption[]) => void;
  /**
   * Controls dropdown virtualization for performance.
   * Auto-enables when options > 50.
   */
  isDropdownVirtualized?: boolean;
};

export const TagGroup = ({
  className,
  isAutoSaveEnabled = false,
  isDropdownVirtualized,
  isEditable = false,
  tagSize = TAG_SIZES.SMALL,
  maxTags,
  value = [],
  tags = [],
  placeholder = 'Select labels to apply',
  emptyOptionsPlaceholder = 'No labels to apply',
  onUpdate = noop,
}: TagGroupProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const blurTimeoutId = useRef<number | null>(null);

  const [isInEditMode, setIsInEditMode] = useState(false);
  const [availableTags, setAvailableTags] = useState(tags); // List of tags currently not selected
  const [selectedTags, setSelectedTags] = useState(value);
  const selectedTagsRef = useRef(selectedTags);

  // Control dropdown open state explicitly
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isRemovingTagRef = useRef(false);

  // Update the ref whenever selectedTags changes
  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);

  const [filter, setFilter] = useState('');

  const visibleTags = useMemo(
    () => (isInEditMode ? selectedTags : value.slice(0, maxTags)).map(tagToVisibleTagOption),
    [value, maxTags, selectedTags, isInEditMode],
  );

  // Tags that are not visible
  const remainderTags = useMemo(
    () => ((maxTags ?? 0) > 0 ? value.slice(maxTags) : []).map(getTagText),
    [maxTags, value],
  );
  const numRemainderTags = remainderTags.length;

  const filteredAvailableSuggestedTags = useMemo(
    () => getFilteredTags(filter, availableTags),
    [filter, availableTags],
  );

  const displayRemainderTag = !isInEditMode && numRemainderTags > 0;
  const displayAddTag = isEditable && !isInEditMode && value.length === 0;
  const displayEditTag = isEditable && !isInEditMode && !displayAddTag;

  const clearBlurTimeout = useCallback(() => {
    if (blurTimeoutId.current) {
      clearTimeout(blurTimeoutId.current);
      blurTimeoutId.current = null;
    }
  }, []);

  const addTag = (newTags: string[] = []) => {
    const uniqueNewTags = uniq(newTags);
    setSelectedTags(uniqueNewTags);
    setAvailableTags(tags.filter((tag) => !uniqueNewTags.includes(tag)));
    setFilter('');

    // For auto-save disabled: close dropdown after selection
    if (!isAutoSaveEnabled) {
      setIsMenuOpen(false);
    }
  };

  const removeTag = useCallback((tag: string) => {
    isRemovingTagRef.current = true;
    setSelectedTags((prevSelectedTags) =>
      uniq(prevSelectedTags.filter((selectedTag) => selectedTag !== tag)),
    );
    setAvailableTags((prevAvailableTags) => uniq([tag, ...prevAvailableTags]));
    // Reset flag after React's update cycle
    requestAnimationFrame(() => {
      isRemovingTagRef.current = false;
    });
  }, []);

  const onEnterEditMode = () => {
    setSelectedTags(value);
    setIsInEditMode(true);
    setAvailableTags(tags); // Reset available tags when entering edit mode
    setIsMenuOpen(true); // Open dropdown when entering edit mode
  };

  /** Returns a list of the remaining tags in the group */
  const onEditModeSave = () => {
    // Use the ref to get the most current value, avoiding stale closure issues
    const currentSelectedTags = selectedTagsRef.current;
    onUpdate(currentSelectedTags.map(getTagText));
    setFilter('');
    setIsInEditMode(false);
    setIsMenuOpen(false);
  };

  /** Returns a list of the remaining tags in the group */
  const onEditModeCancel = () => {
    setSelectedTags(value);
    setAvailableTags(tags);
    setIsInEditMode(false);
    setFilter('');
    setIsMenuOpen(false);
  };

  const handleBlur: React.FocusEventHandler<HTMLDivElement> = () => {
    if (!isInEditMode) return;

    // Clear existing timeout if there is one
    clearBlurTimeout();

    // This needs to be in a timeout so that it only gets run once the next
    // element receives a focus
    blurTimeoutId.current = window.setTimeout(() => {
      const { activeElement } = document;

      // Check if focus moved outside our component
      const isCurrentlyFocusedElementAChild = containerRef.current?.contains(activeElement);
      const shouldSave = !isCurrentlyFocusedElementAChild;

      if (shouldSave && isAutoSaveEnabled) {
        onEditModeSave();
      }
    });
  };

  /**
   * Cleanup effect to clear any pending blur timeout when the component unmounts.
   */
  useEffect(() => {
    return () => {
      clearBlurTimeout();
    };
  }, [clearBlurTimeout]);

  const options = useMemo(
    () => generateTagOptions(filteredAvailableSuggestedTags),
    [filteredAvailableSuggestedTags],
  );

  const MultiValue = useCallback(
    (props: MultiValueGenericProps<BaseOption<string>>) => {
      return (
        <Tag
          size={tagSize}
          text={props?.data.value}
          key={props?.data.value}
          isRemovable={isInEditMode}
          onRemove={(tag = '') => {
            removeTag(tag);
            setIsMenuOpen(true); // Ensure dropdown stays open
          }}
        />
      );
    },
    [isInEditMode, tagSize, removeTag],
  );

  const selectedOptions = useMemo(() => generateTagOptions(selectedTags), [selectedTags]);

  const isMultiSelectVirtualized = isDropdownVirtualized ?? options.length > 50;

  // Handle manual menu open/close
  const handleMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleMenuClose = useCallback(() => {
    // Never close during tag removal
    if (isRemovingTagRef.current) {
      return;
    }

    // For auto-save enabled: always keep menu open
    if (isAutoSaveEnabled) {
      setIsMenuOpen(true);
      return;
    }

    // Otherwise allow closing normally
    setIsMenuOpen(false);
  }, [isAutoSaveEnabled]);

  return (
    <TagGroupWrapper
      className={className}
      data-test="tag-group"
      onBlur={handleBlur}
      ref={containerRef}
    >
      {isInEditMode ? (
        <TagGroupEditWrapper data-test="tag-group-edit-mode">
          <StyledMultiSelect
            usePortal
            autoFocus
            hideSelectedOptions
            hideDropdownIndicator
            isMenuOpen={isMenuOpen}
            onMenuOpen={handleMenuOpen}
            onMenuClose={handleMenuClose}
            closeMenuOnSelect={false}
            Input={TagGroupInput}
            MultiValue={MultiValue}
            customTheme={tagGroupSelectCustomTheme}
            isVirtualized={isMultiSelectVirtualized}
            placeholder={
              filteredAvailableSuggestedTags.length > 0 ? placeholder : emptyOptionsPlaceholder
            }
            options={options}
            value={selectedOptions}
            onSelect={(selected: BaseOption[]) => addTag(selected.map((tag) => tag.value))}
            id="select-new-tag"
          />

          {!isAutoSaveEnabled && (
            <TagGroupActionButtons>
              <StyledActionButton
                variant={BUTTON_VARIANTS.PRIMARY}
                onClick={onEditModeSave}
                data-test="save-edit-tags-button"
              >
                Save
              </StyledActionButton>
              <StyledActionButton
                variant={BUTTON_VARIANTS.SECONDARY}
                onClick={onEditModeCancel}
                data-test="cancel-edit-tags-button"
              >
                Cancel
              </StyledActionButton>
            </TagGroupActionButtons>
          )}
        </TagGroupEditWrapper>
      ) : (
        <TagList
          displayAddTag={displayAddTag}
          displayEditTag={displayEditTag}
          displayRemainderTag={displayRemainderTag}
          isInEditMode={isInEditMode}
          tagSize={tagSize}
          remainderTags={remainderTags}
          visibleTags={visibleTags}
          onEnterEditMode={onEnterEditMode}
          onRemoveTag={removeTag}
        />
      )}
    </TagGroupWrapper>
  );
};
